import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import styles from './BottomSheet.module.css'

/**
 * 모바일 우선 바텀시트(토스식). 화면 하단에서 올라오는 패널 + 하단 고정 액션.
 * packages/ui가 아니라 apps/web 로컬 프리미티브다(공용화는 후속).
 *
 * - 오버레이 탭·Esc·닫기 버튼으로 닫는다.
 * - 열려 있는 동안 배경 스크롤을 잠근다.
 * - footer는 하단에 고정되고, 본문(children)만 스크롤된다(긴 폼도 저장 버튼이 항상 보인다).
 */
export function BottomSheet({
  title,
  onClose,
  footer,
  children,
}: {
  title: ReactNode
  onClose: () => void
  footer?: ReactNode
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.grip} aria-hidden="true" />
        <div className={styles.head}>
          <h2 className={styles.title}>{title}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="닫기">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
